import type { TFunction } from 'i18next';

export type HourFormat = '24h' | '12h';

const LOCALE_MAP: Record<string, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  ru: 'ru-RU',
};

export function getAppLocale(lang?: string): string {
  if (!lang) return 'en-US';
  const base = lang.split('-')[0];
  return LOCALE_MAP[lang] || LOCALE_MAP[base] || 'en-US';
}

function mondayOfWeekContaining(date: Date): Date {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}

export function getMondayWeekdayLabels(
  lang: string,
  style: 'short' | 'narrow'
): string[] {
  const locale = getAppLocale(lang);
  const monday = mondayOfWeekContaining(new Date());
  const formatter = new Intl.DateTimeFormat(locale, { weekday: style });
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return formatter.format(d);
  });
}

export function formatWeekdayShort(date: Date, lang: string): string {
  return date.toLocaleDateString(getAppLocale(lang), { weekday: 'short' });
}

export function formatWeekdayLong(date: Date, lang: string): string {
  return date.toLocaleDateString(getAppLocale(lang), { weekday: 'long' });
}

export function formatHourLabel(
  hour: number,
  lang: string,
  hourFormat: HourFormat = '24h'
): string {
  if (hourFormat === '24h') {
    return `${hour}:00`;
  }
  const d = new Date(2000, 0, 1, hour, 0, 0, 0);
  return d.toLocaleTimeString(getAppLocale(lang), {
    hour: 'numeric',
    hour12: true,
  });
}

export function formatTimeFromDate(
  date: Date,
  lang: string,
  hourFormat: HourFormat = '24h'
): string {
  if (hourFormat === '24h') {
    const hour = date.getHours();
    const minute = date.getMinutes();
    if (minute === 0) return `${hour}:00`;
    return date.toLocaleTimeString(getAppLocale(lang), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    });
  }
  return date.toLocaleTimeString(getAppLocale(lang), {
    hour: 'numeric',
    minute: date.getMinutes() === 0 ? undefined : '2-digit',
    hour12: true,
  });
}

export function formatDateLong(date: Date, lang: string): string {
  return date.toLocaleDateString(getAppLocale(lang), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export function formatMonthShort(date: Date, lang: string): string {
  return date.toLocaleDateString(getAppLocale(lang), { month: 'short' });
}

export function formatSlotAriaLabel(opts: {
  day: string;
  hour: number;
  count: number;
  total?: number;
  lang: string;
  hourFormat?: HourFormat;
  t: TFunction;
}): string {
  const { day, hour, count, total, lang, hourFormat = '24h', t } = opts;
  const [y, m, d] = day.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const dayName = formatWeekdayLong(date, lang);
  const time = formatHourLabel(hour, lang, hourFormat);
  const availability =
    total != null
      ? t('calendar.slot.availableOf', { count, total })
      : t('calendar.slot.available', { count });
  return `${dayName}, ${time} — ${availability}`;
}

export function formatMonthDayAriaLabel(opts: {
  date: Date;
  count: number;
  lang: string;
  t: TFunction;
}): string {
  const { date, count, lang, t } = opts;
  const label = formatDateLong(date, lang);
  if (count <= 0) return label;
  return `${label} — ${t('calendar.slot.available', { count })}`;
}
